/* DOMŪS SAVAGE — pre-launch countdown + locked "View drop" button.
   The launch moment is entered as a wall-clock date and time in an IANA
   time zone (default America/Los_Angeles) and converted to an exact
   instant here, so daylight-saving changes are handled for you. */
(function () {
  'use strict';

  /* Milliseconds that `tz` is ahead of UTC at instant `ts`. */
  function zoneOffset(ts, tz) {
    var f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    var p = {};
    f.formatToParts(new Date(ts)).forEach(function (x) { p[x.type] = x.value; });
    var asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
    return asUTC - Math.floor(ts / 1000) * 1000;
  }

  /* "2026-10-05" + "12:00" in "America/Los_Angeles" -> epoch ms. */
  function zonedToEpoch(dateStr, timeStr, tz) {
    var d = /^(\d{4})-(\d{2})-(\d{2})$/.exec((dateStr || '').trim());
    var t = /^(\d{1,2}):(\d{2})$/.exec((timeStr || '').trim());
    if (!d || !t) return NaN;
    var wall = Date.UTC(+d[1], +d[2] - 1, +d[3], +t[1], +t[2], 0);
    try {
      var guess = wall - zoneOffset(wall, tz);
      guess = wall - zoneOffset(guess, tz);          // second pass settles DST edges
      return guess;
    } catch (e) { return NaN; }                     // unknown time zone name
  }

  function launchLabel(epoch, tz) {
    try {
      var day = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric' }).format(epoch);
      var time = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(epoch);
      return day + ' · ' + time.replace(':00 ', ' ');
    } catch (e) { return ''; }
  }

  var pad = function (n) { return String(n).padStart(2, '0'); };

  /* Difference between the server's clock and this device's clock, in ms.
     Shared by the landing page and the product page; fetched once. */
  var skewPromise = null;
  function serverSkew() {
    if (skewPromise) return skewPromise;
    skewPromise = new Promise(function (resolve) {
      if (!window.fetch) { resolve(0); return; }
      var t0 = Date.now();
      fetch(window.location.pathname, { method: 'HEAD', cache: 'no-store', credentials: 'same-origin' })
        .then(function (r) {
          var server = Date.parse(r.headers.get('date') || '');
          if (isNaN(server)) { resolve(0); return; }
          var skew = server - (t0 + Date.now()) / 2;
          resolve(Math.abs(skew) > 2000 && Math.abs(skew) < 365 * 864e5 ? skew : 0);
        })
        .catch(function () { resolve(0); });
    });
    return skewPromise;
  }

  class DsLaunch extends HTMLElement {
    connectedCallback() {
      this.tz = this.dataset.tz || 'America/Los_Angeles';
      this.launch = zonedToEpoch(this.dataset.date, this.dataset.time, this.tz);
      /* Optional closing moment: after launch the same clock counts down to it. */
      this.close = zonedToEpoch(this.dataset.closeDate, this.dataset.closeTime, this.tz);
      this.clock = this.querySelector('.ds-drop__clock');
      this.clockLabel = this.querySelector('[data-clock-label]');
      this.skew = 0;
      this.units = {};
      var self = this;
      ['d', 'h', 'm', 's'].forEach(function (k) { self.units[k] = self.querySelector('[data-unit="' + k + '"]'); });
      this.dateEl = this.querySelector('[data-launch-label]');
      this.lockBtn = this.querySelector('[data-locked]');
      this.liveMsg = this.querySelector('[data-live-msg]');
      this.status = this.querySelector('[data-launch-status]');

      if (this.dataset.fit === 'true') this.setupFit();

      if (isNaN(this.launch)) { this.setAttribute('data-invalid', ''); return; }
      this.setAttribute('data-launch-epoch', String(this.launch));
      if (this.dateEl && !this.dateEl.textContent.trim()) this.dateEl.textContent = launchLabel(this.launch, this.tz);

      if (this.lockBtn) {
        this.lockBtn.addEventListener('click', function () {
          if (self.status) self.status.textContent = (self.dataset.lockedAnnouncement || 'Opens') + ' ' + launchLabel(self.launch, self.tz) + '.';
        });
      }

      this.tick();
      this.timer = setInterval(function () { self.tick(); }, 1000);
      this.syncClock();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.ro) this.ro.disconnect();
      if (this.fit) { window.removeEventListener('resize', this.fit); window.removeEventListener('orientationchange', this.fit); }
      document.documentElement.classList.remove('ds-noscroll');
    }

    /* One screen, no scrolling: pin the page to the visible screen and, if the
       middle block (heading, form, consent text) is taller or wider than the
       space between the two bands, scale it down just enough to fit. Runs again
       whenever the screen or the form changes size (rotation, form errors,
       the thank-you message). */
    setupFit() {
      var self = this;
      this.stage = this.querySelector('.ds-drop__stage');
      this.main = this.querySelector('.ds-drop__main');
      if (!this.stage || !this.main) return;
      document.documentElement.classList.add('ds-noscroll');
      this.classList.add('is-fit');
      var queued = 0;
      this.fit = function () {
        if (queued) return;
        queued = requestAnimationFrame(function () {
          queued = 0;
          var k = Math.min(1, self.stage.clientHeight / (self.main.offsetHeight || 1), self.stage.clientWidth / (self.main.offsetWidth || 1));
          var want = k < 0.999 ? 'scale(' + k.toFixed(4) + ')' : '';
          if (self.main.style.transform !== want) self.main.style.transform = want;
          self.setAttribute('data-fit-scale', k < 0.999 ? k.toFixed(3) : '1');
        });
      };
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(this.fit);
        this.ro.observe(this.stage);
        this.ro.observe(this.main);
      }
      window.addEventListener('resize', this.fit);
      window.addEventListener('orientationchange', this.fit);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.fit);
      this.fit();
    }

    /* Use the server's clock when we can, so a wrong phone clock
       doesn't unlock the button early or late. */
    syncClock() {
      var self = this;
      serverSkew().then(function (skew) { if (skew) { self.skew = skew; if (!self.hasAttribute('data-closed') && (!self.hasAttribute('data-live') || !isNaN(self.close))) self.tick(); } });
    }
    now() { return (window.__dsNow ? window.__dsNow() : Date.now()) + this.skew; }

    tick() {
      var now = this.now();
      var left = this.launch - now;
      if (left <= 0) {
        this.unlock();
        if (isNaN(this.close)) return;
        left = this.close - now;
        if (left <= 0) { this.closeOut(); return; }
        this.setClockLabel(this.dataset.closeLabel || 'Presale closes in');
      }
      this.render(left);
    }

    render(left) {
      var x = left;
      var d = Math.floor(x / 864e5); x -= d * 864e5;
      var h = Math.floor(x / 36e5);  x -= h * 36e5;
      var m = Math.floor(x / 6e4);   x -= m * 6e4;
      var s = Math.floor(x / 1e3);
      if (this.units.d) this.units.d.textContent = d;
      if (this.units.h) this.units.h.textContent = pad(h);
      if (this.units.m) this.units.m.textContent = pad(m);
      if (this.units.s) this.units.s.textContent = pad(s);
    }

    zero() {
      var self = this;
      ['d', 'h', 'm', 's'].forEach(function (k) { if (self.units[k]) self.units[k].textContent = k === 'd' ? '0' : '00'; });
    }
    setClockLabel(text) {
      if (!this.clockLabel || this.clockLabel.textContent === text) return;
      this.clockLabel.textContent = text;
      this.clockLabel.hidden = !text;
      if (this.clock && text) this.clock.setAttribute('aria-label', text);
    }
    /* The closing moment has passed: stop the clock at zero and say so. */
    closeOut() {
      if (this.hasAttribute('data-closed')) return;
      clearInterval(this.timer);
      this.setAttribute('data-closed', '');
      this.zero();
      this.setClockLabel(this.dataset.closedLabel || 'Presale closed');
      if (this.status) this.status.textContent = '';        // no more "The drop is open." under the button
      this.dispatchEvent(new CustomEvent('ds:close', { bubbles: true }));
    }

    unlock() {
      if (this.hasAttribute('data-live')) return;
      if (isNaN(this.close)) clearInterval(this.timer);   // with a closing moment the clock keeps running
      this.setAttribute('data-live', '');
      this.zero();
      if (this.lockBtn) {
        var href = this.lockBtn.dataset.href;
        if (href) {
          var a = document.createElement('a');
          a.className = this.lockBtn.dataset.liveClass || 'ds-btn ds-btn--chrome ds-drop__cta';
          a.href = href;
          a.setAttribute('data-unlocked', '');
          a.innerHTML = '<span></span><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 8h11M9 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';
          a.querySelector('span').textContent = this.lockBtn.dataset.liveLabel || 'View drop';
          this.lockBtn.replaceWith(a);
          this.lockBtn = null;
        }
      }
      if (this.liveMsg) this.liveMsg.hidden = false;
      if (this.status) this.status.textContent = this.dataset.liveAnnouncement || 'The drop is open.';
      this.dispatchEvent(new CustomEvent('ds:launch', { bubbles: true }));
    }
  }
  if (!customElements.get('ds-launch')) customElements.define('ds-launch', DsLaunch);

  window.DomusLaunch = { zonedToEpoch: zonedToEpoch, launchLabel: launchLabel, serverSkew: serverSkew };
})();
